import React, { useMemo, useState } from 'react';

import { Typography } from '@mui/material';
import type {
  GridActionsColDef,
  GridColDef,
  GridRenderCellParams,
  GridSortModel,
} from '@mui/x-data-grid-premium';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import {
  DocumentLicenseCell,
  DocumentNameAndBadgeCell,
  ListEmpty,
  OwnerCell,
} from 'src/components';
import { GridAlineTooltip } from 'src/components/generic/data-grid/grid-aline-tooltip';
import { DocumentIcon } from 'src/components/icons/DocumentIcon';
import { IconColors } from 'src/components/icons/icon-utils';
import type { Apollo } from 'src/gql/generated';
import { overflowMenuCSSId } from 'src/helpers';
import { createDataGridPremiumPaperStyles, useGetWorkflowFiles } from 'src/hooks';
import type { FileVersionSubset, UserSubset } from 'src/types';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(_theme => ({
  emptyIcon: {
    height: 32,
    width: 32,
  },
  listContainer: {
    flexGrow: 1,
    minHeight: 320,
  },
  xGrid: {
    '& .MuiDataGrid-row': {
      [`&:hover #${overflowMenuCSSId}`]: {
        visibility: 'visible',
      },
    },
  },
}));

interface DocumentRow extends Pick<
  Apollo.Document,
  'id' | 'isCommunityTemplate' | 'name' | 'requesterFavorited' | 'variant' | '__typename'
> {
  latestVersion: FileVersionSubset;
  license: Pick<Apollo.SupportedLicense, 'id' | 'name' | 'url' | '__typename'> | null;
  owner: UserSubset;
  type: Pick<Apollo.ContractType, 'id' | '__typename' | 'value'> | null;
  workflow: Pick<Apollo.Workflow, 'id' | '__typename'>;
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type DocumentGridDef<CellValue = any, FormattedValue = any> =
  | GridColDef<DocumentRow, CellValue, FormattedValue>
  | GridActionsColDef;

interface CommunityWorkflowDocumentsProps {
  workflowId: UUID;
}

export function CommunityWorkflowDocuments({
  workflowId,
}: CommunityWorkflowDocumentsProps): JSX.Element {
  const { classes } = useStyles();
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'name' satisfies keyof DocumentRow, sort: 'asc' },
  ]);
  const { loading, workflowFiles } = useGetWorkflowFiles(workflowId, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });

  const columnDefs: [
    DocumentGridDef<DocumentRow['name']>,
    DocumentGridDef<DocumentRow['owner']>,
    DocumentGridDef<DocumentRow['type']>,
    DocumentGridDef<DocumentRow['license']>,
  ] = [
    {
      field: 'name',
      flex: 1.5,
      headerName: 'Document Name',
      renderCell: (params: GridRenderCellParams<DocumentRow>) => (
        <DocumentNameAndBadgeCell {...params} />
      ),
    },
    {
      field: 'owner',
      flex: 1,
      headerName: 'Owner',
      renderCell: (params: GridRenderCellParams<DocumentRow>) => <OwnerCell {...params} />,
    },
    {
      field: 'type',
      flex: 1,
      headerName: 'Type',
      renderCell: ({ value: contractType }: GridRenderCellParams<DocumentRow>) => (
        <Typography>{contractType?.value ?? '-'}</Typography>
      ),
    },
    {
      field: 'license',
      flex: 1,
      headerName: 'License',
      renderCell: (params: GridRenderCellParams<DocumentRow>) => (
        <DocumentLicenseCell {...params} />
      ),
      sortComparator: (a: DocumentRow['license'], b: DocumentRow['license']) => {
        const aName = a?.name ?? '';
        const bName = b?.name ?? '';
        return aName.localeCompare(bName);
      },
    },
  ];
  const rows = useMemo(() => {
    const documents = workflowFiles.map(workflowFile => workflowFile.document);
    return documents.map(
      (d): DocumentRow => ({
        ...d,
        workflow: { __typename: 'Workflow', id: workflowId },
      })
    );
  }, [workflowFiles, workflowId]);

  return (
    <>
      <Typography variant="subtitle1">Documents</Typography>
      <div className={classes.listContainer}>
        <DataGridPremium<DocumentRow>
          disableColumnFilter
          disableColumnMenu
          disableColumnReorder
          disableColumnResize
          disableRowSelectionOnClick
          hideFooter
          className={classes.xGrid}
          columns={columnDefs}
          getRowHeight={() => 'auto'}
          loading={loading}
          rows={rows}
          slotProps={{
            loadingOverlay: {
              noRowsVariant: 'linear-progress',
              variant: 'linear-progress',
            },
          }}
          slots={{
            baseTooltip: GridAlineTooltip,
            noRowsOverlay: CommunityWorkflowDocumentsEmpty,
          }}
          sortModel={sortModel}
          sx={createDataGridPremiumPaperStyles}
          onSortModelChange={setSortModel}
        />
      </div>
    </>
  );
}

function CommunityWorkflowDocumentsEmpty(): JSX.Element {
  const { classes } = useStyles();

  return (
    <ListEmpty
      icon={<DocumentIcon className={classes.emptyIcon} fill={IconColors.LightGray} />}
      title="No documents"
    />
  );
}
